import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { Mail } from 'lucide-react'
import { SignUp } from '@/components/SignUp'
import { supabase } from '@/auth/supabase'
import { Button } from '@/components/ui/button'

/**
 * Convert Supabase auth errors into user-friendly messages
 * Uses Supabase error codes and status codes for reliable error detection
 *
 * Supabase AuthError structure:
 * - name: string (e.g., 'AuthApiError', 'AuthRetryableFetchError')
 * - message: string (error description)
 * - status: number (HTTP status code: 400, 422, 429, 500, etc.)
 * - code: string (specific error code when available)
 *
 * @see https://supabase.com/docs/reference/javascript/auth-error-codes
 */
function getAuthErrorMessage(error: any): string {
  // Priority 1: Check for specific error codes (most reliable)
  if (error.code) {
    switch (error.code) {
      case 'user_already_exists':
        return `An account with this email already exists. If you've previously created an account with OAuth, please use that to log in.`
      case 'email_not_confirmed':
        return 'Please verify your email address before logging in'
      case 'weak_password':
        return 'Password must be at least 6 characters'
      case 'over_email_send_rate_limit':
      case 'over_request_rate_limit':
        return 'Too many attempts. Please try again in a few minutes'
      case 'validation_failed':
        return 'Please check your email and password format'
      case 'signup_disabled':
        return 'New registrations are currently disabled'
      default:
        // Unhandled code - log for debugging in development
        if (import.meta.env.DEV) {
          console.warn('Unhandled Supabase error code:', error.code, error)
        }
    }
  }

  // Priority 2: Check HTTP status codes (second most reliable)
  if (error.status) {
    switch (error.status) {
      case 400:
        // Invalid credentials typically come as 400 without specific code
        if (error.message?.toLowerCase().includes('invalid')) {
          return 'Invalid email or password'
        }
        return 'Invalid request. Please check your input'
      case 422:
        return 'Invalid email or password format'
      case 429:
        return 'Too many attempts. Please try again in a few minutes'
      case 500:
      case 502:
      case 503:
        return 'Server error. Please try again later'
    }
  }

  // Priority 3: Check error type/name
  if (error.name === 'AuthRetryableFetchError') {
    return 'Network error. Please check your connection and try again'
  }

  // Priority 4: Fallback to message parsing (least reliable, but catches edge cases)
  const msg = error.message?.toLowerCase() || ''

  if (msg.includes('invalid login credentials')) {
    return 'Invalid email or password'
  }
  if (msg.includes('not authorized')) {
    return 'You are not authorized to perform this action'
  }
  if (msg.includes('network') || msg.includes('fetch failed')) {
    return 'Network error. Please check your connection'
  }

  // Last resort: return original message or generic error
  return error.message || 'An error occurred. Please try again'
}

const SignupSchema = z.object({
  email: z.email('Please enter a valid email'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})

const signupSearchSchema = z.object({
  redirect: z.string().optional().default('/'),
})

export const Route = createFileRoute('/signup')({
  validateSearch: signupSearchSchema,
  beforeLoad: ({ context, search }) => {
    if (context.session) {
      throw redirect({ to: search.redirect })
    }
  },
  component: SignupComp,
})

function SignupComp() {
  const { redirect: redirectUrl } = Route.useSearch()

  const signupMutation = useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const parsed = SignupSchema.parse(data)

      const { data: authData, error } = await supabase.auth.signUp({
        email: parsed.email,
        password: parsed.password,
        options: {
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      })

      if (error) {
        const errorMessage = getAuthErrorMessage(error)
        throw new Error(errorMessage)
      }

      const userId = authData.user?.id
      if (!userId) {
        throw new Error('Failed to create user')
      }

      return {
        message: 'Signup successful! Please verify your email.',
      }
    },
    onError: (err) => {
      console.error('error signing up', err)
    },
  })

  // Get user-friendly error message
  const errorMessage = signupMutation.error
    ? (() => {
        const err = signupMutation.error
        const msg = err.message || ''

        // Check if it's a Zod validation error (comes as JSON string in error message)
        if (msg.startsWith('[') || msg.startsWith('{')) {
          try {
            const zodErrors = JSON.parse(msg)
            // If it's an array of Zod errors, return the first error's message
            if (Array.isArray(zodErrors) && zodErrors.length > 0) {
              return zodErrors[0].message
            }
          } catch {
            // If JSON parsing fails, fall through to returning the raw message
          }
        }

        // Return the error message as-is (already processed by server)
        return msg || 'Signup failed, please try again'
      })()
    : null

  // Show success screen after successful signup
  if (signupMutation.status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md w-full space-y-8 text-center">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold">Welcome!</h1>

            <div className="flex justify-center py-8">
              <div className="rounded-full bg-primary/10 p-8">
                <Mail className="w-24 h-24 text-primary" />
              </div>
            </div>

            <div className="space-y-2">
              <h2 className="text-2xl font-semibold">Signup Successful!</h2>
              <p className="text-muted-foreground">
                Please check your email and verify your account before logging
                in.
              </p>
              <p className="text-muted-foreground">
                If you're having trouble receiving an email, please check your
                spam account.
              </p>
            </div>
          </div>

          <Button asChild className="w-full bg-primary hover:bg-primary/90">
            <Link to="/login" search={{ redirect: redirectUrl }}>
              Go to Login
            </Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <SignUp
      status={signupMutation.status}
      redirectUrl={redirectUrl}
      onSubmit={(e) => {
        const formData = new FormData(e.target as HTMLFormElement)

        signupMutation.mutate({
          email: formData.get('email') as string,
          password: formData.get('password') as string,
        })
      }}
      afterSubmit={
        errorMessage ? (
          <p className="text-red-400 text-center">{errorMessage}</p>
        ) : null
      }
    />
  )
}
